import React from 'react';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { Alert, Avatar, Box, IconButton, TextField, Typography } from '@mui/material';
import AdminFormRow from '../../../../components/admin/AdminFormRow';
import AdminSectionHeading from '../../../../components/admin/AdminSectionHeading';
import { adminColors, adminRadii, adminTypography } from '../../../../styles/adminDesignTokens';
import {
  adminAlertSx,
  adminAlertToneSx,
  adminCombineSx,
  adminFileInputOverlaySx,
  adminFormFieldSx,
  adminIconActionButtonSx,
  adminUploadDropzoneContentSx,
  adminUploadDropzoneSx,
} from '../../../../styles/adminComponentStyles';

interface GameDialogImageFieldProps {
  imageError?: string;
  imageUrlError?: string;
  existingImageUrl: string;
  hasSelectedImage: boolean;
  imagePreview: string;
  imageUrl: string;
  loading: boolean;
  onImageSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onImageUrlChange: (value: string) => void;
  onRemoveImage: () => void;
}

const GameDialogImageField: React.FC<GameDialogImageFieldProps> = ({
  imageError,
  imageUrlError,
  existingImageUrl,
  hasSelectedImage,
  imagePreview,
  imageUrl,
  loading,
  onImageSelect,
  onImageUrlChange,
  onRemoveImage,
}) => (
  <>
    <AdminSectionHeading>Image</AdminSectionHeading>
    <AdminFormRow label="Image">
      <Box>
        <Box sx={adminCombineSx(adminUploadDropzoneSx, { minHeight: 104 })}>
          <Box sx={adminCombineSx(adminUploadDropzoneContentSx, { minHeight: 104 })} width="100%">
            {(imagePreview || existingImageUrl) ? (
              <Box sx={{ alignItems: 'center', display: 'flex' }}>
                <Avatar
                  src={imagePreview || existingImageUrl}
                  variant="rounded"
                  sx={{ width: 96, height: 88, borderRadius: adminRadii.sm, '& .MuiAvatar-img': { objectFit: 'cover' } }}
                />
                <IconButton
                  aria-label="Remove selected image"
                  size="small"
                  onClick={onRemoveImage}
                  sx={adminCombineSx(adminIconActionButtonSx, {
                    color: adminColors.danger,
                    position: 'absolute',
                    right: 4,
                    top: 4,
                  })}
                >
                  <DeleteOutlineRoundedIcon fontSize="inherit" />
                </IconButton>
              </Box>
            ) : (
              <>
                <UploadFileRoundedIcon fontSize="small" />
                <Typography className="AdminUploadDropzone-label">
                  Upload Image
                </Typography>
                <Box
                  component="input"
                  disabled={loading || !!imageUrl}
                  type="file"
                  accept="image/*"
                  onChange={onImageSelect}
                  aria-label="Upload game image"
                  sx={adminFileInputOverlaySx}
                />
              </>
            )}
          </Box>
        </Box>

        {imageError && (
          <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger, { mt: 1 })}>
            {imageError}
          </Alert>
        )}

        <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText, display: 'block', mt: 0.75, textAlign: 'end' }}>
          Supported formats: JPG, PNG. Max size: 10MB
        </Typography>
      </Box>
    </AdminFormRow>

    <AdminFormRow label="Image URL">
      <TextField
        fullWidth
        size="small"
        placeholder="https://example.com/image.jpg"
        value={imageUrl}
        onChange={(event) => onImageUrlChange(event.target.value)}
        error={!!imageUrlError}
        helperText={imageUrlError || 'Provide an image URL as an alternative to file upload'}
        disabled={loading || hasSelectedImage || !!existingImageUrl}
        sx={adminFormFieldSx}
        inputProps={{ 'aria-label': 'Image URL' }}
      />
    </AdminFormRow>
  </>
);

export default React.memo(GameDialogImageField);
